/**
 * Prayer Times Service
 * Fetches Shia-specific prayer times using Aladhan API
 */

import { getEasternTime } from './timezoneService';

// Aladhan API configuration for Shia Ithna Ashari
const ALADHAN_CONFIG = {
  method: 0,        // Shia Ithna Ashari (Leva Institute, Qum)
  school: 1,        // Jafari/Shia
  midnightMode: 0,  // Standard (halfway between sunset and fajr)
  latitudeAdjustmentMethod: 3, // One-Seventh rule
  tune: "0,0,0,0,0,0,0,0,0", // No adjustments (can be customized per prayer)
  adjustment: 0     // Hijri calendar adjustment in days (-2 to +2)
};

// Cache configuration
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const CACHE_KEY_PREFIX = 'prayerTimes_';
const CACHE_VERSION = '4.0'; // Increment this to invalidate old cache - Changed to fix Hijri date issue

/**
 * Get coordinates from ZIP code using free API
 * @param {string} zipCode - US ZIP code
 * @returns {Promise<{lat: number, lng: number, city: string}>}
 */
async function getCoordinatesFromZip(zipCode) {
  try {
    // Using zippopotam.us free API
    const response = await fetch(`https://api.zippopotam.us/us/${zipCode}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get coordinates for ZIP ${zipCode}`);
    }
    
    const data = await response.json();
    
    return {
      lat: parseFloat(data.places[0].latitude),
      lng: parseFloat(data.places[0].longitude),
      city: data.places[0]['place name']
    };
  } catch (error) {
    console.error('Error getting coordinates from ZIP:', error);
    // Fallback to Detroit if ZIP lookup fails
    return {
      lat: 42.3314,
      lng: -83.0458,
      city: 'Detroit'
    };
  }
}

/**
 * Fetch Shia prayer times from Aladhan API
 * @param {string} zipCode - ZIP code for location
 * @param {Date} date - Date to fetch times for (optional, defaults to today)
 * @returns {Promise<Object>} Prayer times object
 */
export async function fetchShiaPrayerTimes(zipCode, date = new Date()) {
  try {
    // Format date consistently for cache key (YYYY-MM-DD format for consistency)
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const cacheDateStr = `${year}-${month}-${day}`;
    
    // Check cache first
    const cacheKey = `${CACHE_KEY_PREFIX}${zipCode}_${cacheDateStr}`;
    const cached = getCachedData(cacheKey);
    if (cached) {
      console.log('Returning cached prayer times');
      return cached;
    }

    // Get coordinates from ZIP
    const coords = await getCoordinatesFromZip(zipCode);
    
    // Format date for API in DD-MM-YYYY format (Aladhan API preferred format)
    // Using already declared day, month, year variables from above
    const dateStr = `${day}-${month}-${year}`;
    console.log('Fetching prayer times for date:', dateStr, 'from Date object:', date);
    
    // Build API URL with Shia-specific parameters
    const url = new URL(`https://api.aladhan.com/v1/timings/${dateStr}`);
    url.searchParams.append('latitude', coords.lat);
    url.searchParams.append('longitude', coords.lng);
    url.searchParams.append('method', ALADHAN_CONFIG.method);
    url.searchParams.append('school', ALADHAN_CONFIG.school);
    url.searchParams.append('midnightMode', ALADHAN_CONFIG.midnightMode);
    url.searchParams.append('latitudeAdjustmentMethod', ALADHAN_CONFIG.latitudeAdjustmentMethod);
    url.searchParams.append('tune', ALADHAN_CONFIG.tune);
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error('Failed to fetch prayer times');
    }
    
    const data = await response.json();
    
    if (data.code !== 200 || !data.data) {
      throw new Error('Invalid response from prayer times API');
    }
    
    // Extract sunset time for Maghrib calculation
    const sunsetTime = data.data.timings.Sunset;
    
    // Calculate Maghrib time (18 minutes after sunset for Shia)
    const maghribTime = calculateMaghribTime(sunsetTime);
    
    console.log('Prayer Times Calculation Debug:', {
      sunsetOriginal: sunsetTime,
      maghribCalculated: maghribTime,
      sunsetTimeComponents: sunsetTime.split(':'),
      maghribTimeComponents: maghribTime.split(':'),
      diff: `Should be 18 minutes after sunset`
    });
    
    // Log Hijri date from API
    console.log('API Response - Hijri Date:', {
      fullHijri: data.data.date.hijri,
      day: data.data.date.hijri.day,
      month: data.data.date.hijri.month,
      year: data.data.date.hijri.year,
      gregorianDate: data.data.date.gregorian.date,
      apiUrl: url.toString()
    });
    
    // Validate Hijri year is reasonable (not stuck at 1441)
    const currentYear = new Date().getFullYear();
    const hijriYear = parseInt(data.data.date.hijri.year);
    
    // Hijri year should be roughly currentYear - 579 (±2 years for calendar differences)
    const expectedHijriYear = currentYear - 579;
    if (Math.abs(hijriYear - expectedHijriYear) > 2) {
      console.error('Invalid Hijri year from API:', hijriYear, 'Expected around:', expectedHijriYear);
      console.error('API returned date:', data.data.date);
      // Don't throw error, but log warning - API might be correct for edge cases
      if (hijriYear === 1441 && currentYear > 2020) {
        console.warn('WARNING: API returned year 1441 which is likely incorrect for', currentYear);
      }
    }
    
    // Extract and format prayer times
    const prayerTimes = {
      fajr: data.data.timings.Fajr,
      sunrise: data.data.timings.Sunrise,
      dhuhr: data.data.timings.Dhuhr,
      asr: data.data.timings.Asr,
      sunset: sunsetTime, // Keep sunset time visible
      maghrib: maghribTime, // Maghrib is 18 minutes after sunset for Shia
      isha: data.data.timings.Isha,
      midnight: data.data.timings.Midnight,
      // Additional info
      gregorian: {
        date: data.data.date.gregorian.date,
        weekday: data.data.date.gregorian.weekday.en,
        month: data.data.date.gregorian.month.en,
        year: data.data.date.gregorian.year
      },
      hijri: {
        date: data.data.date.hijri.date,
        weekday: data.data.date.hijri.weekday.en,
        month: data.data.date.hijri.month.en,
        year: data.data.date.hijri.year,
        day: data.data.date.hijri.day
      },
      location: {
        city: coords.city,
        latitude: coords.lat,
        longitude: coords.lng
      },
      timezone: data.data.meta.timezone,
      fetchedAt: new Date().toISOString(),
      cacheVersion: CACHE_VERSION // Add version to cached data
    };
    
    // Cache the result
    setCachedData(cacheKey, prayerTimes);
    
    return prayerTimes;
  } catch (error) {
    console.error('Error fetching Shia prayer times:', error);
    throw error;
  }
}

/**
 * Calculate Maghrib time (18 minutes after sunset)
 * @param {string} sunsetTime - Sunset time in HH:MM format
 * @returns {string} Maghrib time in HH:MM format
 */
function calculateMaghribTime(sunsetTime) {
  const [hours, minutes] = sunsetTime.split(':').map(Number);
  const totalMinutes = hours * 60 + minutes + 18; // Add 18 minutes
  
  const maghribHours = Math.floor(totalMinutes / 60) % 24;
  const maghribMinutes = totalMinutes % 60;
  
  return `${maghribHours.toString().padStart(2, '0')}:${maghribMinutes.toString().padStart(2, '0')}`;
}

/**
 * Clear all prayer times cache
 */
export function clearPrayerTimesCache() {
  try {
    const keys = Object.keys(localStorage);
    let cleared = 0;
    keys.forEach(key => {
      if (key.startsWith(CACHE_KEY_PREFIX)) {
        localStorage.removeItem(key);
        cleared++;
      }
    });
    console.log(`Cleared ${cleared} cached prayer times entries`);
  } catch (error) {
    console.error('Error clearing prayer times cache:', error);
  }
}

/**
 * Get the next prayer time based on current time
 * @param {Object} prayerTimes - Prayer times object from fetchShiaPrayerTimes
 * @returns {Object} Next prayer info {name, time, remainingMinutes}
 */
export function getNextPrayer(prayerTimes, timezone) {
  if (!prayerTimes) return null;
  
  // Get current time in location timezone
  const now = new Date();
  const nowInLocation = timezone 
    ? new Date(now.toLocaleString("en-US", { timeZone: timezone }))
    : now;
  
  const currentTimeMinutes = nowInLocation.getHours() * 60 + nowInLocation.getMinutes();
  
  const prayers = [
    { name: 'Fajr', time: prayerTimes.fajr },
    { name: 'Sunrise', time: prayerTimes.sunrise },
    { name: 'Dhuhr', time: prayerTimes.dhuhr },
    { name: 'Asr', time: prayerTimes.asr },
    { name: 'Maghrib', time: prayerTimes.maghrib },
    { name: 'Isha', time: prayerTimes.isha }
  ];
  
  for (const prayer of prayers) {
    const [hours, minutes] = prayer.time.split(':').map(Number);
    const prayerTimeMinutes = hours * 60 + minutes;
    
    if (prayerTimeMinutes > currentTimeMinutes) {
      const remainingMinutes = prayerTimeMinutes - currentTimeMinutes;
      return {
        name: prayer.name,
        time: prayer.time,
        remainingMinutes,
        remainingFormatted: formatRemainingTime(remainingMinutes)
      };
    }
  }
  
  // If no prayer left today, return Fajr for tomorrow
  const [fajrHours, fajrMinutes] = prayerTimes.fajr.split(':').map(Number);
  const fajrTimeMinutes = fajrHours * 60 + fajrMinutes;
  const remainingMinutes = (24 * 60 - currentTimeMinutes) + fajrTimeMinutes;
  
  return {
    name: 'Fajr',
    time: prayerTimes.fajr,
    remainingMinutes,
    remainingFormatted: 'Tomorrow',
    isTomorrow: true
  };
}

/**
 * Format remaining time in human-readable format
 * @param {number} minutes - Remaining minutes
 * @returns {string} Formatted time string
 */
function formatRemainingTime(minutes) {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (mins === 0) {
    return `${hours}h`;
  }
  
  return `${hours}h ${mins}m`;
}

/**
 * Convert 24-hour time to 12-hour format
 * @param {string} time24 - Time in 24-hour format (HH:MM)
 * @returns {string} Time in 12-hour format (h:mm AM/PM)
 */
export function convertTo12Hour(time24) {
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
}

/**
 * Get cached data from localStorage
 * @param {string} key - Cache key
 * @returns {Object|null} Cached data or null if expired/not found
 */
function getCachedData(key) {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    
    const data = JSON.parse(cached);
    const now = new Date().getTime();
    
    // Check cache version - invalidate old cache
    if (!data.cacheVersion || data.cacheVersion !== CACHE_VERSION) {
      console.log('Invalidating old cache version:', data.cacheVersion || 'none', 'vs', CACHE_VERSION);
      localStorage.removeItem(key);
      return null;
    }
    
    // Check if required fields exist (sunset was added later)
    if (!data.sunset || !data.maghrib) {
      console.log('Invalidating cache - missing required fields');
      localStorage.removeItem(key);
      return null;
    }
    
    // Check if cache is expired
    if (now - new Date(data.fetchedAt).getTime() > CACHE_DURATION) {
      localStorage.removeItem(key);
      return null;
    }
    
    return data;
  } catch (error) {
    console.error('Error reading from cache:', error);
    return null;
  }
}

/**
 * Set cached data in localStorage
 * @param {string} key - Cache key
 * @param {Object} data - Data to cache
 */
function setCachedData(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error('Error writing to cache:', error);
    // Continue without caching
  }
}

/**
 * Get Hijri month name by month number
 * @param {number} monthNumber - Month number (1-12)
 * @returns {string} Month name in English
 */
function getHijriMonthNameByNumber(monthNumber) {
  const monthNames = [
    '', // 0 - not used
    'Muharram',
    'Safar',
    "Rabi' al-awwal",
    "Rabi' al-thani",
    'Jumada al-awwal',
    'Jumada al-thani',
    'Rajab',
    "Sha'ban",
    'Ramadan',
    'Shawwal',
    "Dhu al-Qi'dah",
    'Dhu al-Hijjah'
  ];
  return monthNames[monthNumber] || 'Unknown';
}

/**
 * Adjust Hijri date for moon sighting differences
 * @param {Object} hijriDate - Original Hijri date object
 * @param {number} adjustment - Days to adjust (-2 to +2)
 * @returns {Object} Adjusted Hijri date
 */
export function adjustHijriDate(hijriDate, adjustment = 0) {
  if (!hijriDate || adjustment === 0) return hijriDate;

  // Parse the existing date
  const day = parseInt(hijriDate.day);
  const adjustedDay = day + adjustment;

  // Parse month NUMBER from the date string (more reliable than names due to Unicode issues)
  const monthNumber = parseInt(hijriDate.date.split('-')[1]);

  // Get current month length from API data (not hardcoded!)
  const currentMonthLength = parseInt(hijriDate.month?.days) || 30;

  let newDay = adjustedDay;
  let newMonthNumber = monthNumber;
  let newYear = parseInt(hijriDate.year);

  // Handle month boundaries
  if (adjustedDay < 1) {
    // Go to previous month
    newMonthNumber = monthNumber === 1 ? 12 : monthNumber - 1;
    // Assume 30 days for previous month (Islamic months are 29 or 30)
    // We use 30 to be safe - the actual length will be correct next time API is called
    const prevMonthLength = 30;
    newDay = prevMonthLength + adjustedDay; // adjustedDay is 0 or negative

    // Handle year boundary (going from Muharram back to Dhu al-Hijjah)
    if (monthNumber === 1) {
      newYear = newYear - 1;
    }
  } else if (adjustedDay > currentMonthLength) {
    // Go to next month
    newDay = adjustedDay - currentMonthLength;
    newMonthNumber = monthNumber === 12 ? 1 : monthNumber + 1;

    // Handle year boundary (going from Dhu al-Hijjah to Muharram)
    if (monthNumber === 12) {
      newYear = newYear + 1;
    }
  }

  // Get month name using NUMBER-based lookup (avoids Unicode/ASCII mismatch issues)
  const newMonthName = getHijriMonthNameByNumber(newMonthNumber);

  return {
    ...hijriDate,
    day: newDay.toString(),
    date: `${newDay.toString().padStart(2, '0')}-${newMonthNumber.toString().padStart(2, '0')}-${newYear}`,
    month: {
      ...hijriDate.month,
      number: newMonthNumber,
      en: newMonthName
    },
    year: newYear.toString(),
    adjusted: true,
    adjustmentDays: adjustment
  };
}

/**
 * Get next Hijri month name
 * @param {string} currentMonth - Current month name
 * @returns {string} Next month name
 */
function getNextHijriMonth(currentMonth) {
  const months = {
    'Muharram': 'Safar',
    'Safar': 'Rabi\' al-awwal',
    'Rabi\' al-awwal': 'Rabi\' al-thani',
    'Rabi\' al-thani': 'Jumada al-awwal',
    'Jumada al-awwal': 'Jumada al-thani',
    'Jumada al-thani': 'Rajab',
    'Rajab': 'Sha\'ban',
    'Sha\'ban': 'Ramadan',
    'Ramadan': 'Shawwal',
    'Shawwal': 'Dhu al-Qi\'dah',
    'Dhu al-Qi\'dah': 'Dhu al-Hijjah',
    'Dhu al-Hijjah': 'Muharram'
  };
  return months[currentMonth] || currentMonth;
}

/**
 * Get previous Hijri month name
 * @param {string} currentMonth - Current month name
 * @returns {string} Previous month name
 */
function getPreviousHijriMonth(currentMonth) {
  const months = {
    'Muharram': 'Dhu al-Hijjah',
    'Safar': 'Muharram',
    'Rabi\' al-awwal': 'Safar',
    'Rabi\' al-thani': 'Rabi\' al-awwal',
    'Jumada al-awwal': 'Rabi\' al-thani',
    'Jumada al-thani': 'Jumada al-awwal',
    'Rajab': 'Jumada al-thani',
    'Sha\'ban': 'Rajab',
    'Ramadan': 'Sha\'ban',
    'Shawwal': 'Ramadan',
    'Dhu al-Qi\'dah': 'Shawwal',
    'Dhu al-Hijjah': 'Dhu al-Qi\'dah'
  };
  return months[currentMonth] || currentMonth;
}

/**
 * Get current time for a specific timezone
 * @param {string} timezone - IANA timezone string (e.g., 'America/New_York')
 * @returns {Date} Current date/time - formatting will handle timezone conversion
 */
export function getCurrentTimeForLocation(timezone) {
  // Return current date - the timezone conversion happens during formatting
  // This keeps the Date object consistent for calculations
  return new Date();
}

// Export default object with all functions
const prayerTimesService = {
  fetchShiaPrayerTimes,
  getNextPrayer,
  convertTo12Hour,
  clearPrayerTimesCache,
  adjustHijriDate,
  getCurrentTimeForLocation
};

export default prayerTimesService;